import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import style from "./AddSkills.module.css";
import { baseurl } from "../Url";
import { MdAdd, MdArrowBack } from "react-icons/md";
import { AiOutlineClose } from "react-icons/ai";
import Loader from "../Loder/Loader";
import ToastPopup from "../ToastPopup/ToastPopup";

const AddSkills = () => {
  const navigate = useNavigate();
  const USERID = localStorage.getItem("USERID");
  const OrgID = localStorage.getItem("ORGID");
  const [loading, setLoading] = useState(true);
  const [skillInput, setSkillInput] = useState("");
  const [skills, setSkills] = useState([]);
  const skillInputRef = useRef(null);

  // Suggested skills list
  const suggestedSkills = [
    "Web Development",
    "Object-Oriented Programming (OOP)",
    "Software Development",
    "Application Development",
    "Content Design"
  ];

  const [showSuggestedSkills, setShowSuggestedSkills] = useState(true);
  const [toastPopup, setToastPopup] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToastPopup({ show: true, message, type });
  };
  const closeToast = () => setToastPopup((p) => ({ ...p, show: false }));

  useEffect(() => {
    if (USERID && OrgID) {
      fetchSkillsList();
    } else {
      setLoading(false);
    }
  }, [USERID, OrgID]);

  async function fetchSkillsList() {
    try {
      const response = await fetch(`${baseurl}/candidate/skills/list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: USERID,
          org_id: OrgID,
        }),
      });

      const data = await response.json();
      
      if (data.status === true && data.data) {
        setSkills(Array.isArray(data.data) ? data.data : []);
      } else {
        console.error("Failed to fetch skills:", data.description || "Unknown error");
        setSkills([]);
      }
    } catch (error) {
      console.error("Error fetching skills list:", error);
      setSkills([]);
    } finally {
      setLoading(false);
    }
  }

  const formatSkillName = (skill) => {
    // Handle special cases
    const specialCases = {
      'javascript': 'JavaScript',
      'js': 'JavaScript',
      'python': 'Python',
      'html': 'HTML',
      'css': 'CSS',
      'react': 'React',
      'nodejs': 'Node.js',
      'node.js': 'Node.js',
      'vue': 'Vue.js',
      'vuejs': 'Vue.js',
      'angular': 'Angular',
      'typescript': 'TypeScript',
      'ts': 'TypeScript',
      'php': 'PHP',
      'java': 'Java',
      'c++': 'C++',
      'c#': 'C#',
      'sql': 'SQL',
      'mongodb': 'MongoDB',
      'mysql': 'MySQL',
      'postgresql': 'PostgreSQL',
      'aws': 'AWS',
      'docker': 'Docker',
      'kubernetes': 'Kubernetes',
      'git': 'Git',
      'github': 'GitHub',
      'gitlab': 'GitLab',
      'api': 'API',
      'rest': 'REST',
      'graphql': 'GraphQL',
      'redux': 'Redux',
      'nextjs': 'Next.js',
      'next.js': 'Next.js',
      'express': 'Express.js',
      'expressjs': 'Express.js',
    };

    const lowerSkill = skill.toLowerCase().trim();
    
    // Check if it's a special case
    if (specialCases[lowerSkill]) {
      return specialCases[lowerSkill];
    }
    
    // Capitalize first letter of each word
    return skill
      .split(/\s+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const addSkill = () => {
    if (!skillInput.trim()) return;
    // Split by comma, newline, or semicolon to add multiple skills at once
    const newSkills = skillInput
      .split(/[,\n;]/)
      .map((s) => formatSkillName(s.trim()))
      .filter((s) => s.length > 0 && !isSkillInList(s));
    if (newSkills.length > 0) {
      setSkills([...skills, ...newSkills]);
      setSkillInput("");
    }
  };

  const removeSkill = (skillToRemove) => {
    setSkills(skills.filter((s) => s !== skillToRemove));
  };

  const isSkillInList = (skillToCheck) =>
    skills.some((s) => s.toLowerCase().trim() === skillToCheck.toLowerCase().trim());

  const addSuggestedSkill = (skill) => {
    // Put skill in text bar so user can review and click Add
    setSkillInput((prev) => (prev ? `${prev}, ${skill}` : skill));
    skillInputRef.current?.focus();
  };

  const handleSkillInputKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addSkill();
    }
  };

  const handleSaveSkills = async () => {
    setLoading(true);
    
    // Add any skill in the input field before saving
    let skillsToSave = [...skills];
    if (skillInput.trim()) {
      const formatted = formatSkillName(skillInput.trim());
      if (formatted && !skillsToSave.includes(formatted)) {
        skillsToSave.push(formatted);
      }
    }
    
    try {
      const response = await fetch(`${baseurl}/candidate/profile/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: USERID,
          org_id: OrgID,
          my_skill: skillsToSave,
        }),
      });

      const data = await response.json();
      
      if (data.status === true) {
        showToast("Skills saved successfully!");
        setTimeout(() => navigate("/profile", { state: { activeTab: "Skills" } }), 500);
      } else {
        showToast("Failed to save skills: " + (data.description || "Unknown error"), "error");
      }
    } catch (error) {
      console.error("Error saving skills:", error);
      showToast("An error occurred while saving the skills", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading && skills.length === 0) {
    return <Loader />;
  }

  return (
    <div className={style.container}>
      <div className={style.pageContent}>
        {/* Add Skill Input Section */}
        <div className={style.addSkillsSection}>
          <div className={style.inputContainer}>
            <input
              ref={skillInputRef}
              type="text"
              className={style.skillInputField}
              placeholder="Enter skills (comma, semicolon or newline separated)"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyPress={handleSkillInputKeyPress}
              autoFocus
            />
            <button
              type="button"
              className={style.addButton}
              onClick={addSkill}
              disabled={!skillInput.trim()}
            >
              <MdAdd className={style.addIcon} /> Add
            </button>
          </div>
          <p className={style.hintText}>e.g. React, JavaScript, Python or one per line</p>

          {/* Added Skills List */}
          {skills.length > 0 && (
            <div className={style.skillsListSection}>
              <h3 className={style.sectionTitle}>Skills to add ({skills.length})</h3>
              <div className={style.skillsList}>
                {skills.map((skill, index) => (
                  <div key={`${skill}-${index}`} className={style.skillItem}>
                    <span className={style.skillName}>{skill}</span>
                    <button
                      type="button"
                      className={style.removeButton}
                      onClick={() => removeSkill(skill)}
                      title="Remove"
                    >
                      <AiOutlineClose />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Suggested Skills Section */}
        {showSuggestedSkills && (
          <div className={style.suggestedSkillsSection}>
            <div className={style.suggestedHeader}>
              <h3 className={style.suggestedTitle}>Suggested based on your profile</h3>
              <button 
                className={style.closeButton}
                onClick={() => setShowSuggestedSkills(false)}
              >
                <AiOutlineClose />
              </button>
            </div>
            <div className={style.suggestedSkillsList}>
              {suggestedSkills.map((skill, index) => (
                <button
                  key={index}
                  type="button"
                  className={style.suggestedSkillButton}
                  onClick={() => addSuggestedSkill(skill)}
                >
                  <span className={style.skillName}>{skill}</span>
                  <MdAdd className={style.skillPlusIcon} />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className={style.saveButtonContainer}>
          <button className={style.saveButton} onClick={handleSaveSkills}>
            Save
          </button>
        </div>
      </div>

      <ToastPopup
        show={toastPopup.show}
        message={toastPopup.message}
        type={toastPopup.type}
        onClose={closeToast}
      />
    </div>
  );
};

export default AddSkills;

